/**
 * Shared anonymous Firebase auth for public pages (CareStart /carestart3, Gemini Live, etc.).
 *
 * WHY this exists: callers used to read `auth.currentUser` synchronously and, finding it
 * null during Firebase's asynchronous session restore, immediately called
 * `signInAnonymously()`. Because `currentUser` is null for the first moment after load,
 * that lost the race on essentially every visit/interaction and minted a brand-new
 * anonymous user each time. The repeated sign-ins intermittently trip
 * `auth/too-many-requests` (and are fragile to transient `network-request-failed`), which
 * surfaced to users as a misleading "Unable to connect. Please check your internet
 * connection" screen.
 *
 * This helper instead waits for the first `onAuthStateChanged` emission (so a restored or
 * real user is reused), and only signs in anonymously when there is genuinely no user —
 * retrying transient failures with backoff.
 */

import firebaseApp from "services/firebase";

/** Firebase returns these when Anonymous sign-in is off or disallowed for the project. */
const ANONYMOUS_AUTH_BLOCKED_CODES = new Set([
  "auth/admin-restricted-operation",
  "auth/operation-not-allowed",
]);

/** Codes worth retrying — transient connectivity / rate-limit pressure rather than config. */
const TRANSIENT_AUTH_CODES = new Set([
  "auth/network-request-failed",
  "auth/too-many-requests",
  "auth/internal-error",
]);

/**
 * Safety net only. Firebase fires onAuthStateChanged promptly (with the restored user or
 * null) once it resolves persistence, so we almost never wait this long; the timeout just
 * guarantees we still proceed to sign-in if the listener never fires (e.g. test stubs).
 */
const AUTH_RESTORE_TIMEOUT_MS = 2500;
const MAX_SIGN_IN_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 400;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function enhanceAnonSignInError(err, blockedHint, errorName) {
  const code = err && err.code;
  if (!code || !ANONYMOUS_AUTH_BLOCKED_CODES.has(code)) return err;
  const wrapped = new Error(`${blockedHint} (${code})`);
  wrapped.name = errorName;
  wrapped.code = code;
  wrapped.cause = err;
  return wrapped;
}

/**
 * Resolve to the current Firebase user once auth-state restoration settles.
 * `auth.currentUser` is synchronously null until the persisted session is restored, so we
 * wait for the first onAuthStateChanged tick (bounded by a timeout so we never hang).
 * @param {object} auth Firebase Auth instance
 * @returns {Promise<object | null>}
 */
export function waitForInitialAuthUser(auth) {
  if (auth.currentUser) return Promise.resolve(auth.currentUser);
  return new Promise((resolve) => {
    let settled = false;
    let unsubscribe = () => {};
    const finish = (user) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try {
        unsubscribe();
      } catch {
        /* listener may already be torn down */
      }
      resolve(user || null);
    };
    const timer = setTimeout(() => finish(auth.currentUser), AUTH_RESTORE_TIMEOUT_MS);
    try {
      const maybeUnsub = auth.onAuthStateChanged(
        (user) => finish(user),
        () => finish(auth.currentUser),
      );
      if (typeof maybeUnsub === "function") unsubscribe = maybeUnsub;
    } catch {
      finish(auth.currentUser);
    }
  });
}

/**
 * Ensure a Firebase user (restored, real, or freshly anonymous) exists before any action
 * that requires an authenticated caller.
 * @param {{ logPrefix?: string, errorName?: string, blockedHint?: string }} [options]
 * @returns {Promise<object | null>}
 */
export async function ensureAnonymousFirebaseUser(options = {}) {
  if (typeof window === "undefined") return null;
  const {
    logPrefix = "[auth]",
    errorName = "AnonymousAuthError",
    blockedHint =
      "Enable Anonymous sign-in: Firebase Console → Authentication → Sign-in method → " +
      "Anonymous → Enable, then reload.",
  } = options;

  const auth = firebaseApp.auth();

  const restored = await waitForInitialAuthUser(auth);
  if (restored) return restored;

  let lastErr;
  for (let attempt = 1; attempt <= MAX_SIGN_IN_ATTEMPTS; attempt += 1) {
    // A user may have appeared between attempts (e.g. a parallel caller signed in).
    if (auth.currentUser) return auth.currentUser;
    try {
      const cred = await auth.signInAnonymously();
      return cred.user;
    } catch (e) {
      lastErr = e;
      const code = e && e.code;
      const isLastAttempt = attempt === MAX_SIGN_IN_ATTEMPTS;
      if (!code || !TRANSIENT_AUTH_CODES.has(code) || isLastAttempt) break;
      console.warn(
        `${logPrefix} signInAnonymously transient failure (${code}); ` +
          `retrying ${attempt}/${MAX_SIGN_IN_ATTEMPTS - 1}`,
      );
      await delay(RETRY_BASE_DELAY_MS * 2 ** (attempt - 1));
    }
  }

  const mapped = enhanceAnonSignInError(lastErr, blockedHint, errorName);
  console.error(`${logPrefix} signInAnonymously failed`, mapped.cause || mapped);
  throw mapped;
}
